/**
 * =============================================================================
 * ROASTBREW - CATALOG ERROR OVERLAY
 * =============================================================================
 *
 * Shown when the content catalog could not be loaded AT ALL. The app is
 * genuinely unusable in that state — the catalog is the entire content tree, so
 * Library, Courses, Search and the video navigator have nothing to render — and
 * this says so instead of pretending otherwise.
 *
 * ── WHAT THIS REPLACED ───────────────────────────────────────────────────────
 * A failed catalog fetch used to call setCatalog(PLACEHOLDER_CATALOG). The user
 * then browsed a FABRICATED catalogue — invented lesson names, invented
 * structure — and got 404s on every click, with no error shown anywhere. An
 * outage was indistinguishable from working software. That is the single worst
 * behaviour the placeholder data caused, and it is what this component exists to
 * end.
 *
 * ── IT MUST NOT FIRE ON A BACKGROUND FAILURE ─────────────────────────────────
 * App.jsx's ensureFresh re-fetches the catalog mid-session to re-mint expiring
 * signed CDN URLs. If THAT fetch fails, the user still has a perfectly good
 * catalog on screen and must not be interrupted. So the mount condition in
 * App.jsx is `token && catalogError && catalog.length === 0` — the overlay
 * appears only when there is nothing to fall back to. The retry is silent in
 * every other case.
 *
 * ── PLACEMENT ────────────────────────────────────────────────────────────────
 * Rendered inside AppInner (it needs catalog state), as a position:fixed layer
 * above everything. Same practical effect as UnsupportedBrowserGate: while it is
 * up, the app cannot be reached.
 *
 * Styled with its own local colors — it can render before ThemeProvider has
 * anything meaningful, and it must work when the app around it does not.
 *
 * ── TEMPORARY BY DESIGN ──────────────────────────────────────────────────────
 * This is a blunt instrument: total failure, total block. A future version
 * should degrade per-surface (Library empty state, Courses retry banner) rather
 * than taking the whole app down. Until that exists, blocking with an honest
 * message beats silently serving fiction.
 *
 * PROPS
 * @param {function} onRetry   fire a catalog refetch
 * @param {boolean}  retrying  a retry is currently in flight
 * =============================================================================
 */

import React, { useEffect, useRef, useState } from 'react';

// Backoff schedule in seconds. Retries automatically, slowing down, and then
// stops asking — at that point the failure is not transient and hammering the
// API helps nobody. The manual button always works.
const BACKOFF_S = [5, 10, 20, 40, 60];

export default function CatalogErrorOverlay({ onRetry = () => {}, retrying = false }) {
  const [attempt, setAttempt] = useState(0);
  const [countdown, setCountdown] = useState(BACKOFF_S[0]);
  const onRetryRef = useRef(onRetry);
  useEffect(() => { onRetryRef.current = onRetry; });

  // TWO timers, deliberately separate:
  //   setTimeout  fires the retry, exactly once per attempt
  //   setInterval only counts the display down
  //
  // The first version did both inside a setCountdown UPDATER, calling onRetry and
  // setAttempt from within it. React requires updaters to be PURE, and index.js
  // wraps the app in <React.StrictMode>, which double-invokes updaters precisely
  // to surface that — so every retry fired TWO catalog fetches. Side effects
  // belong in the effect body, not in a state updater.
  //
  // Bumping `attempt` re-runs this effect with the next backoff. Both timers are
  // cleared on cleanup, so StrictMode's mount/unmount/mount cannot double-fire.
  useEffect(() => {
    if (attempt >= BACKOFF_S.length) return undefined;
    const wait = BACKOFF_S[Math.min(attempt, BACKOFF_S.length - 1)];
    setCountdown(wait);
    const tick = setInterval(() => setCountdown((c) => (c > 0 ? c - 1 : 0)), 1000);
    const fire = setTimeout(() => {
      onRetryRef.current();
      setAttempt((a) => a + 1);
    }, wait * 1000);
    return () => { clearInterval(tick); clearTimeout(fire); };
  }, [attempt]);

  const manual = () => {
    setAttempt(0);
    setCountdown(BACKOFF_S[0]);
    onRetryRef.current();
  };

  const exhausted = attempt >= BACKOFF_S.length;

  return (
    <div className="rb-cat-err" role="alert" aria-live="assertive">
      <div className="rb-cat-err__box">
        <div className="rb-cat-err__mark">
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#C9A962"
            strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 3a9 9 0 1 0 9 9" />
            <path d="M12 7v5" /><path d="M12 16h.01" />
          </svg>
        </div>

        <h1 className="rb-cat-err__h">We can’t reach your courses</h1>

        <p className="rb-cat-err__p">
          The content library didn’t load, so there’s nothing for the app to show
          yet. This is almost always temporary — we’re trying again automatically.
        </p>

        <div className="rb-cat-err__status">
          {retrying
            ? <span className="rb-cat-err__spin" aria-hidden="true" />
            : null}
          <span>
            {retrying
              ? 'Reconnecting…'
              : exhausted
                ? 'Automatic retries have stopped.'
                : `Trying again in ${countdown}s`}
          </span>
        </div>

        <button type="button" className="rb-cat-err__btn" onClick={manual} disabled={retrying}>
          {retrying ? 'Reconnecting…' : 'Try now'}
        </button>

        <p className="rb-cat-err__foot">
          Still stuck? Reload the page, or check back in a few minutes.
        </p>
      </div>

      <style>{`
        .rb-cat-err{position:fixed;inset:0;z-index:9000;display:flex;align-items:center;
          justify-content:center;padding:24px;background:#0D0D0D;
          font-family:"DM Sans",-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;
          color:#F5F5F0;
          background-image:radial-gradient(ellipse at 50% 30%, rgba(201,169,98,0.08) 0%, transparent 58%)}
        .rb-cat-err *{box-sizing:border-box}
        .rb-cat-err__box{max-width:440px;width:100%;text-align:center}
        .rb-cat-err__mark{width:72px;height:72px;border-radius:50%;margin:0 auto 26px;
          display:flex;align-items:center;justify-content:center;
          background:rgba(201,169,98,0.10);border:1px solid rgba(201,169,98,0.28)}
        .rb-cat-err__h{font-family:"Cormorant Garamond",Georgia,serif;font-weight:300;
          font-size:clamp(26px,5vw,34px);line-height:1.15;letter-spacing:-.01em;margin:0 0 14px}
        .rb-cat-err__p{font-size:15px;line-height:1.65;color:#A0A090;margin:0 0 28px}
        .rb-cat-err__status{display:flex;align-items:center;justify-content:center;gap:10px;
          min-height:24px;font-size:13px;color:#A0A090;margin-bottom:20px;
          font-variant-numeric:tabular-nums}
        .rb-cat-err__spin{width:14px;height:14px;border-radius:50%;flex-shrink:0;
          border:2px solid rgba(245,245,240,0.2);border-top-color:#C9A962;
          animation:rb-cat-spin .8s linear infinite}
        @keyframes rb-cat-spin{to{transform:rotate(360deg)}}
        .rb-cat-err__btn{min-height:50px;padding:0 30px;border-radius:11px;border:none;
          background:#C9A962;color:#0D0D0D;font-family:inherit;font-size:15px;font-weight:500;
          cursor:pointer;transition:background .25s,opacity .25s}
        .rb-cat-err__btn:disabled{opacity:.55;cursor:default}
        .rb-cat-err__btn:focus-visible{outline:2px solid #E8D5A3;outline-offset:3px}
        @media (hover:hover){.rb-cat-err__btn:not(:disabled):hover{background:#E8D5A3}}
        .rb-cat-err__foot{font-size:13px;color:#A0A090;opacity:.7;margin:24px 0 0}
        @media (prefers-reduced-motion:reduce){
          .rb-cat-err__spin{animation-duration:.001ms}
        }
      `}</style>
    </div>
  );
}
